import { MessageCircle } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils/cn';

/**
 * Floating help button that opens the Plain chat support widget.
 *
 * Renders a circular button fixed to the bottom-right corner of the viewport
 * with a subtle hover tooltip. Hidden when the Plain widget is not available
 * or on pages where it overlaps embedded viewers (e.g. /scenarios).
 */
export function HelpButton() {
  const [isAvailable, setIsAvailable] = React.useState(false);
  const { pathname } = useLocation();

  // Hide on the scenarios page where telemetry viewers overlap the button
  const isScenariosPage = pathname === '/scenarios';

  // Poll for Plain widget availability (it loads asynchronously).
  // Stop after 10 seconds to avoid polling indefinitely if the script fails.
  React.useEffect(() => {
    if (window.Plain) {
      setIsAvailable(true);
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;

    const interval = setInterval(() => {
      attempts += 1;

      if (window.Plain) {
        setIsAvailable(true);
        clearInterval(interval);
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  if (!isAvailable || isScenariosPage) return null;

  return (
    <div className="fixed right-5 bottom-5 z-[999]">
      <div className="group relative">
        {/* Tooltip */}
        <div
          className={cn(
            'pointer-events-none absolute top-1/2 right-full mr-3 -translate-y-1/2',
            'whitespace-nowrap rounded-lg px-3 py-1.5',
            'bg-gray-900 text-white text-xs shadow-lg dark:bg-gray-100 dark:text-gray-900',
            'opacity-0 transition-opacity duration-200 group-hover:opacity-100'
          )}
        >
          Need help?
        </div>

        {/* Button */}
        <button
          type="button"
          onClick={() => window.Plain?.open()}
          className={cn(
            'flex size-11 items-center justify-center rounded-full shadow-lg',
            'bg-gray-900 text-white transition-all duration-200 ease-out hover:bg-gray-800 hover:shadow-xl',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2',
            'dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100'
          )}
          aria-label="Open help chat"
        >
          <MessageCircle className="size-5" />
        </button>
      </div>
    </div>
  );
}

export default HelpButton;
